import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Divider,
  Stack,
  Grid,
} from '@mui/material';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { ToastContainer, toast } from 'react-toastify';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import axiosInstance from '../../../services/axiosInstance';
import { subscriptionTypes } from '../../../constants/subscriptionTypes';

const isActiveStatus = status => status === 'active' || status === 'trialing';

const UserSubscriptions = ({ user, updateUser, navigate }) => {
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    subscriptionId: null,
  });

  const cancelSubscription = async subscriptionId => {
    try {
      const response = await axiosInstance.post('/users/cancel-subscription', {
        subscriptionId,
      });

      if (response.success) {
        toast.success('Suscripción cancelada.');
        const updatedSubscriptions = user.subscriptions.map(sub =>
          sub.id === subscriptionId ? { ...sub, status: 'cancel' } : sub
        );
        updateUser('subscriptions', updatedSubscriptions);
      } else {
        toast.error('Error al cancelar la suscripción.');
      }
    } catch (error) {
      console.error('Error al cancelar la suscripción:', error);
      toast.error('Error inesperado al cancelar la suscripción');
    }
  };

  const reactivateSubscription = async subscriptionId => {
    try {
      const response = await axiosInstance.post(
        '/users/reactivate-subscription',
        { subscriptionId }
      );

      if (response.success) {
        toast.success('Suscripción reactivada.');
        const updatedSubscriptions = user.subscriptions.map(sub =>
          sub.id === subscriptionId ? { ...sub, status: 'active' } : sub
        );
        updateUser('subscriptions', updatedSubscriptions);
      } else {
        toast.error('Error al reactivar la suscripción.');
      }
    } catch (error) {
      console.error('Error al reactivar la suscripción:', error);
      toast.error('Error inesperado al reactivar la suscripción');
    }
  };

  const openConfirmationDialog = subscriptionId => {
    setConfirmationDialog({ open: true, subscriptionId });
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialog({ open: false, subscriptionId: null });
  };

  const handleConfirmCancel = () => {
    if (confirmationDialog.subscriptionId) {
      cancelSubscription(confirmationDialog.subscriptionId);
    }
    closeConfirmationDialog();
  };

  const membershipTypeIds = ['adult', 'youth', 'padel'];

  const hasActiveMembershipSubscription =
    user.subscriptions?.some(subscription => {
      const isMembership = membershipTypeIds.includes(subscription.type);

      if (!isMembership) {
        return false;
      }

      const isCanceledButStillActive =
        subscription.status === 'canceled' &&
        subscription.currentPeriodEnd &&
        new Date(subscription.currentPeriodEnd) > new Date();

      return isActiveStatus(subscription.status) || isCanceledButStillActive;
    }) ?? false;

  return (
    <Card sx={{ mt: 4 }}>
      <ToastContainer />
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Detalles de las Suscripciones
        </Typography>
        <Divider sx={{ my: 2 }} />
        {user.subscriptions &&
          user.subscriptions.length > 0 &&
          user.subscriptions.map(subscription => {
            const isActive = isActiveStatus(subscription.status);
            const isCanceledButStillActive =
              subscription.status === 'canceled' &&
              subscription.currentPeriodEnd &&
              new Date(subscription.currentPeriodEnd) > new Date();

            return (
              <Box
                key={subscription.id}
                sx={{
                  mb: 4,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: isCanceledButStillActive
                    ? 'rgba(255, 165, 0, 0.1)'
                    : isActive
                      ? 'rgba(0, 128, 0, 0.1)'
                      : 'rgba(255, 0, 0, 0.1)',
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <Typography variant="subtitle1" gutterBottom>
                      <strong>Tipo:</strong>{' '}
                      {subscriptionTypes[subscription.type] || 'N/A'}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {isCanceledButStillActive ? (
                        <>
                          <CancelIcon color="warning" />
                          <Typography color="warning.main">
                            Cancelada (activa hasta{' '}
                            {new Date(
                              subscription.currentPeriodEnd
                            ).toLocaleDateString()}
                            )
                          </Typography>
                        </>
                      ) : isActive ? (
                        <>
                          <CheckCircleIcon color="success" />
                          <Typography color="success.main">Activa</Typography>
                        </>
                      ) : (
                        <>
                          <CancelIcon color="error" />
                          <Typography color="error.main">
                            Completamente cancelada
                          </Typography>
                        </>
                      )}
                    </Stack>
                    <Typography>
                      <strong>Próxima renovación:</strong>{' '}
                      {subscription.currentPeriodEnd
                        ? new Date(
                            subscription.currentPeriodEnd
                          ).toLocaleDateString()
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Stack direction="row" spacing={1}>
                      {isCanceledButStillActive && (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<RestartAltIcon />}
                          onClick={() =>
                            reactivateSubscription(subscription.id)
                          }
                        >
                          Reactivar
                        </Button>
                      )}
                      {isActive && (
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() =>
                            openConfirmationDialog(subscription.id)
                          }
                        >
                          Cancelar
                        </Button>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
              </Box>
            );
          })}
        {!hasActiveMembershipSubscription && (
          <>
            <Typography>No tienes una suscripción de socio activa.</Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={() => navigate('/membership')}
            >
              Hazte Socio
            </Button>
          </>
        )}
      </CardContent>
      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={closeConfirmationDialog}
        onConfirm={handleConfirmCancel}
        title="Cancelar Suscripción"
        message="¿Estás seguro de que deseas cancelar esta suscripción?"
        confirmText="Cancelar Suscripción"
        cancelText="Volver"
      />
    </Card>
  );
};

export default UserSubscriptions;
